import * as React from "react";
import {
  Badge,
  Box,
  ButtonBase,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import ProfileImage from "./Avatar";
import { brand } from "../theme";
import { canApprove } from "./navItems";

export default function Header({ onMenuClick, onLogout }) {
  const user = JSON.parse(secureLocalStorage.getItem("profile") || "null");
  const navigate = useNavigate();
  const pending = useSelector((state) => state.unApprovedInsurance) || [];
  const monthly = useSelector((state) => state.monthly) || [];
  const [noticeEl, setNoticeEl] = React.useState(null);
  const [userEl, setUserEl] = React.useState(null);

  const approver = canApprove(user);
  const notices = [
    approver &&
      pending.length > 0 && {
        icon: <FactCheckOutlinedIcon fontSize="small" color="primary" />,
        text: `${pending.length} insurance ${pending.length === 1 ? "policy waits" : "policies wait"} for approval`,
        to: "/insurance/approvals",
      },
    monthly.length > 0 && {
      icon: <EventBusyOutlinedIcon fontSize="small" color="warning" />,
      text: `${monthly.length} ${monthly.length === 1 ? "policy expires" : "policies expire"} this month`,
      to: "/insurance/",
    },
  ].filter(Boolean);

  const go = (to) => {
    setNoticeEl(null);
    setUserEl(null);
    navigate(to);
  };

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (t) => t.zIndex.appBar,
        height: 64,
        px: { xs: 1.5, md: 3 },
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: alpha("#fff", 0.9),
        backdropFilter: "blur(8px)",
        borderBottom: `1px solid ${brand.border}`,
      }}
    >
      <Tooltip title="Toggle menu">
        <IconButton onClick={onMenuClick} edge="start" aria-label="Toggle menu">
          <MenuIcon />
        </IconButton>
      </Tooltip>
      <Typography
        sx={{ fontWeight: 600, color: "text.secondary", display: { xs: "none", sm: "block" } }}
      >
        Sekyere Community Bank PLC
      </Typography>

      <Box sx={{ flex: 1 }} />

      <Tooltip title="Notifications">
        <IconButton onClick={(e) => setNoticeEl(e.currentTarget)} aria-label="Notifications">
          <Badge color="error" variant="dot" invisible={!notices.length}>
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <ButtonBase
        onClick={(e) => setUserEl(e.currentTarget)}
        sx={{ ml: 0.5, borderRadius: 2, px: 1, py: 0.5, gap: 1.25, "&:hover": { bgcolor: "action.hover" } }}
      >
        <ProfileImage size={34} editable={false} />
        <Box sx={{ textAlign: "left", display: { xs: "none", md: "block" } }}>
          <Typography sx={{ fontSize: 13, fontWeight: 650, lineHeight: 1.2 }}>
            {user?.results?.firstName} {user?.results?.lastName}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary", lineHeight: 1.2 }}>
            {user?.results?.risk_manager || user?.results?.userRole || ""}
          </Typography>
        </Box>
      </ButtonBase>

      {/* Notifications */}
      <Menu
        anchorEl={noticeEl}
        open={Boolean(noticeEl)}
        onClose={() => setNoticeEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: 1, width: 320, border: `1px solid ${brand.border}` } } }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
          Notifications
        </Typography>
        <Divider />
        {notices.length ? (
          notices.map((n) => (
            <MenuItem key={n.text} onClick={() => go(n.to)} sx={{ whiteSpace: "normal", py: 1.25 }}>
              <ListItemIcon>{n.icon}</ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: 13 }} primary={n.text} />
            </MenuItem>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
            You are all caught up.
          </Typography>
        )}
      </Menu>

      {/* User */}
      <Menu
        anchorEl={userEl}
        open={Boolean(userEl)}
        onClose={() => setUserEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 230, border: `1px solid ${brand.border}` } } }}
      >
        <Stack sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ fontWeight: 650, fontSize: 14 }}>
            {user?.results?.firstName} {user?.results?.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.results?.email}
          </Typography>
        </Stack>
        <Divider />
        <MenuItem onClick={() => go("/insurance/user-page")}>
          <ListItemIcon>
            <SettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem
          onClick={() => {
            setUserEl(null);
            onLogout();
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "inherit" }}>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Log out
        </MenuItem>
      </Menu>
    </Box>
  );
}
